import * as styles from './Button.css';
import { Button as Interface } from './types';
import { ButtonHTMLAttributes, memo, PropsWithChildren } from 'react';
import Loading from '../Loading';

type BaseButtonProps = Interface.Props &
  Pick<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'disabled' | 'children' | 'onClick' | 'className' | 'style'
  >;

type AnchorButtonProps = Interface.Props &
  Pick<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    'href' | 'children' | 'className' | 'style' | 'onClick'
  > & {
    as: 'a';
  };

type NativeButtonProps = BaseButtonProps & {
  as?: 'button';
};

type PropTypes = AnchorButtonProps | NativeButtonProps;

type LoadingContentSwitchProps = Pick<PropTypes, 'loading'>;

function isTextNonePadding(
  props: PropTypes,
): props is PropTypes & Interface.PropsIfTextNonePadding {
  return props.type === 'textNonePadding';
}

const Button = (props: PropTypes) => {
  if (props.as === 'a') {
    const { href, category, type, size, className, children, ...rest } = props;
    return (
      <a
        href={href}
        className={`${styles.container({
          category,
          type,
          size,
          underline: isTextNonePadding(props) ? props.underline : undefined,
        })} ${className}`}
        {...rest}
      >
        {children}
      </a>
    );
  }

  const {
    loading = false,
    category,
    type,
    size,
    children,
    className,
    ...rest
  } = props;

  return (
    <button
      className={`${styles.container({
        category,
        type,
        size,
        underline: isTextNonePadding(props) ? props.underline : undefined,
      })} ${className}`}
      {...rest}
    >
      <LoadingContentSwitch loading={loading}>{children}</LoadingContentSwitch>
    </button>
  );
};

const LoadingContentSwitch = memo(
  ({ children, loading }: PropsWithChildren<LoadingContentSwitchProps>) =>
    loading ? <Loading.Circle width={24} height={24} /> : children,
);

LoadingContentSwitch.displayName = 'ButtonLoadingContentSwitch';

export default Button;
